import type { Editor } from '@tiptap/core'
import type { Node } from '@tiptap/pm/model'

/**
 * Shared blockquote toggle function that handles list items properly
 */
export function toggleBlockquote(editor: Editor) {
  if (!editor || editor.isDestroyed) return
  
  const { from, to, empty } = editor.state.selection
  const selectedText = editor.state.doc.textBetween(from, to, ' ')
  
  // Check if we're currently in a list by looking at parent nodes
  const resolvedPos = editor.state.selection.$from
  let nodeAtDepth: Node | null = null
  let isInList = false
  
  // Check all parent nodes to see if we're in a list structure
  for (let i = resolvedPos.depth; i >= 0; i--) {
    nodeAtDepth = resolvedPos.node(i)
    if (nodeAtDepth && (nodeAtDepth.type.name === 'bulletList' || nodeAtDepth.type.name === 'orderedList')) {
      isInList = true
      break
    }
  }
  
  if (isInList) {
    // If in a list, we need to convert the list item to a paragraph first
    // then apply blockquote
    if (!empty && selectedText.trim()) {
      // Get the content and create a blockquote manually
      editor.chain()
        .focus()
        .deleteRange({ from, to })
        .insertContent([
          { type: 'blockquote', content: [{ type: 'paragraph', content: [{ type: 'text', text: selectedText }] }] }
        ])
        .run()
    } else {
      // Try to lift the list item and convert to blockquote
      const listItemPos = resolvedPos
      let listItemNode: Node | null = null
      let listItemDepth = -1
      
      // Find the list item node
      for (let i = listItemPos.depth; i >= 0; i--) {
        if (listItemPos.node(i).type.name === 'listItem') {
          listItemNode = listItemPos.node(i)
          listItemDepth = i
          break
        }
      }
      
      if (listItemNode) {
        // Get the text content of the list item
        const listItemText = listItemNode.textContent
        
        // Find the position before the list item to check if we need to clean up
        const listItemStart = listItemPos.before(listItemDepth)
        const listItemEnd = listItemPos.after(listItemDepth)
        
        // Check if this is the only item in the list
        const parentList = listItemPos.node(listItemDepth - 1)
        const isOnlyItem = parentList && parentList.childCount === 1
        
        if (isOnlyItem) {
          // If it's the only item, replace the entire list with blockquote
          const listStart = listItemPos.before(listItemDepth - 1)
          const listEnd = listItemPos.after(listItemDepth - 1)
          
          editor.chain()
            .focus()
            .deleteRange({ from: listStart, to: listEnd })
            .insertContent([
              { type: 'blockquote', content: [{ type: 'paragraph', content: [{ type: 'text', text: listItemText }] }] }
            ])
            .run()
        } else {
          // If there are other items, just replace this item
          editor.chain()
            .focus()
            .deleteRange({ from: listItemStart, to: listItemEnd })
            .insertContent([
              { type: 'blockquote', content: [{ type: 'paragraph', content: [{ type: 'text', text: listItemText }] }] }
            ])
            .run()
        }
      }
    }
  } else if (!empty && selectedText.trim()) {
    // Has text selection (not in list)
    const resolvedFrom = editor.state.doc.resolve(from)
    const resolvedTo = editor.state.doc.resolve(to)
    const startPos = resolvedFrom.before(resolvedFrom.depth)
    const endPos = resolvedTo.after(resolvedTo.depth)
    
    if (startPos === endPos) {
      // Selection is within a single block
      // Split the block and apply blockquote to selected text with spacing
      editor.chain()
        .focus()
        .deleteRange({ from, to })
        .insertContent([
          { type: 'paragraph' },
          { type: 'blockquote', content: [{ type: 'paragraph', content: [{ type: 'text', text: selectedText }] }] },
          { type: 'paragraph' }
        ])
        .run()
    } else {
      // Selection spans multiple blocks
      // Check if already in blockquote
      if (editor.isActive('blockquote')) {
        editor.chain()
          .focus()
          .setTextSelection({ from, to })
          .lift('blockquote')
          .run()
      } else {
        editor.chain()
          .focus()
          .setTextSelection({ from, to })
          .wrapIn('blockquote')
          .run()
      }
    }
  } else {
    // No selection, toggle current block
    if (editor.isActive('blockquote')) {
      editor.chain().focus().lift('blockquote').run()
    } else {
      editor.chain().focus().wrapIn('blockquote').run()
    }
  }
}